import React, { useState, useEffect } from 'react';
import {
  Table,
  Card,
  Space,
  Button,
  DatePicker,
  Select,
  Input,
  Tag,
  Typography,
  Row,
  Col,
  Statistic,
  Divider,
  message
} from 'antd';
import {
  SearchOutlined,
  FilterOutlined,
  DownloadOutlined,
  ReloadOutlined
} from '@ant-design/icons';
import dayjs, { Dayjs } from 'dayjs';
import type { ColumnsType } from 'antd/es/table';
import {
  GeneralLedgerLine,
  GeneralLedgerFilters,
  journalService
} from '@/services/journalService';
import { Account } from '@/services/accountService';

const { RangePicker } = DatePicker;
const { Title, Text } = Typography;

interface GeneralLedgerProps {
  accounts: Account[];
}

const GeneralLedger: React.FC<GeneralLedgerProps> = ({ accounts }) => {
  const [loading, setLoading] = useState(false);
  const [ledgerLines, setLedgerLines] = useState<GeneralLedgerLine[]>([]);
  const [filters, setFilters] = useState<GeneralLedgerFilters>({});
  const [searchText, setSearchText] = useState('');

  // Statistics
  const [totalDebit, setTotalDebit] = useState(0);
  const [totalCredit, setTotalCredit] = useState(0);
  const [balance, setBalance] = useState(0);

  useEffect(() => {
    fetchLedger();
  }, [filters]);

  useEffect(() => {
    // Calculate totals
    const debitSum = ledgerLines.reduce((sum, line) => sum + Number(line.debit), 0);
    const creditSum = ledgerLines.reduce((sum, line) => sum + Number(line.credit), 0);
    setTotalDebit(debitSum);
    setTotalCredit(creditSum);
    setBalance(debitSum - creditSum);
  }, [ledgerLines]);

  const fetchLedger = async () => {
    setLoading(true);
    try {
      const lines = await journalService.getGeneralLedger(filters);
      setLedgerLines(lines);
    } catch (error: any) {
      message.error(error.message || 'Failed to fetch general ledger');
    } finally {
      setLoading(false);
    }
  };

  const handleDateRangeChange = (dates: [Dayjs | null, Dayjs | null] | null) => {
    if (dates && dates[0] && dates[1]) {
      setFilters({
        ...filters,
        dateFrom: dates[0].toISOString(),
        dateTo: dates[1].toISOString()
      });
    } else {
      const { dateFrom, dateTo, ...rest } = filters;
      setFilters(rest);
    }
  };

  const handleAccountChange = (accountId: string | undefined) => {
    if (accountId) {
      setFilters({ ...filters, accountId });
    } else {
      const { accountId: _, ...rest } = filters;
      setFilters(rest);
    }
  };

  const handleReset = () => {
    setFilters({});
    setSearchText('');
  };

  const handleExport = () => {
    message.info('Export feature coming soon');
    // TODO: Implement CSV/Excel export
  };

  // Filter lines by search text
  const filteredLines = ledgerLines.filter(line => {
    if (!searchText) return true;
    const search = searchText.toLowerCase();
    return (
      line.account.code.toLowerCase().includes(search) ||
      line.account.name.toLowerCase().includes(search) ||
      line.entry.name.toLowerCase().includes(search) ||
      (line.entry.ref && line.entry.ref.toLowerCase().includes(search)) ||
      (line.entry.narration && line.entry.narration.toLowerCase().includes(search)) ||
      line.entry.journal.code.toLowerCase().includes(search)
    );
  });

  // Calculate running balance
  const linesWithBalance = filteredLines.map((line, index) => {
    let runningBalance = 0;
    for (let i = 0; i <= index; i++) {
      runningBalance += Number(filteredLines[i].debit) - Number(filteredLines[i].credit);
    }
    return {
      ...line,
      runningBalance
    };
  });

  const columns: ColumnsType<GeneralLedgerLine & { runningBalance: number }> = [
    {
      title: 'Date',
      dataIndex: ['entry', 'date'],
      key: 'date',
      width: 120,
      sorter: (a, b) => new Date(a.entry.date).getTime() - new Date(b.entry.date).getTime(),
      render: (date: string) => dayjs(date).format('YYYY-MM-DD')
    },
    {
      title: 'Entry #',
      dataIndex: ['entry', 'name'],
      key: 'entryName',
      width: 120,
      render: (name: string) => <Text code>{name}</Text>
    },
    {
      title: 'Journal',
      dataIndex: ['entry', 'journal', 'code'],
      key: 'journal',
      width: 100,
      render: (code: string, record) => (
        <Tag color="blue">{code}</Tag>
      )
    },
    {
      title: 'Account',
      key: 'account',
      width: 250,
      render: (_, record) => (
        <div>
          <Text strong>{record.account.code}</Text>
          <br />
          <Text type="secondary" style={{ fontSize: '12px' }}>
            {record.account.name}
          </Text>
        </div>
      )
    },
    {
      title: 'Description',
      key: 'description',
      width: 200,
      render: (_, record) => (
        <div>
          {record.name && <div>{record.name}</div>}
          {record.entry.ref && (
            <Text type="secondary" style={{ fontSize: '12px' }}>
              Ref: {record.entry.ref}
            </Text>
          )}
        </div>
      )
    },
    {
      title: 'Debit',
      dataIndex: 'debit',
      key: 'debit',
      width: 120,
      align: 'right',
      render: (debit: number) => (
        <Text strong style={{ color: debit > 0 ? '#3f8600' : undefined }}>
          {debit > 0 ? `$${Number(debit).toFixed(2)}` : '-'}
        </Text>
      )
    },
    {
      title: 'Credit',
      dataIndex: 'credit',
      key: 'credit',
      width: 120,
      align: 'right',
      render: (credit: number) => (
        <Text strong style={{ color: credit > 0 ? '#cf1322' : undefined }}>
          {credit > 0 ? `$${Number(credit).toFixed(2)}` : '-'}
        </Text>
      )
    },
    {
      title: 'Balance',
      dataIndex: 'runningBalance',
      key: 'balance',
      width: 120,
      align: 'right',
      render: (balance: number) => (
        <Text strong style={{ color: balance >= 0 ? '#3f8600' : '#cf1322' }}>
          ${Math.abs(balance).toFixed(2)}
          {balance < 0 && ' CR'}
        </Text>
      )
    }
  ];

  return (
    <div>
      <Card>
        <Title level={4}>General Ledger</Title>
        <Text type="secondary">
          View all posted journal entry transactions. Filter by date range, account, or search.
        </Text>

        <Divider />

        {/* Filters */}
        <Space direction="vertical" size="middle" style={{ width: '100%' }}>
          <Row gutter={16}>
            <Col span={8}>
              <RangePicker
                style={{ width: '100%' }}
                onChange={handleDateRangeChange}
                placeholder={['Start Date', 'End Date']}
                format="YYYY-MM-DD"
              />
            </Col>
            <Col span={8}>
              <Select
                allowClear
                showSearch
                style={{ width: '100%' }}
                placeholder="Filter by account"
                onChange={handleAccountChange}
                filterOption={(input, option) =>
                  (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                }
                options={accounts
                  .filter(acc => !acc.deprecated)
                  .map(acc => ({
                    value: acc.id,
                    label: `${acc.code} - ${acc.name}`
                  }))}
              />
            </Col>
            <Col span={8}>
              <Input
                placeholder="Search entries..."
                prefix={<SearchOutlined />}
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
                allowClear
              />
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={24}>
              <Space>
                <Button icon={<ReloadOutlined />} onClick={fetchLedger}>
                  Refresh
                </Button>
                <Button icon={<FilterOutlined />} onClick={handleReset}>
                  Reset Filters
                </Button>
                <Button
                  icon={<DownloadOutlined />}
                  onClick={handleExport}
                  disabled={filteredLines.length === 0}
                >
                  Export
                </Button>
              </Space>
            </Col>
          </Row>
        </Space>
      </Card>

      {/* Statistics */}
      <Card style={{ marginTop: 16, background: '#fafafa' }}>
        <Row gutter={16}>
          <Col span={6}>
            <Statistic
              title="Total Transactions"
              value={filteredLines.length}
              prefix="#"
            />
          </Col>
          <Col span={6}>
            <Statistic
              title="Total Debit"
              value={totalDebit}
              precision={2}
              prefix="$"
              valueStyle={{ color: '#3f8600' }}
            />
          </Col>
          <Col span={6}>
            <Statistic
              title="Total Credit"
              value={totalCredit}
              precision={2}
              prefix="$"
              valueStyle={{ color: '#cf1322' }}
            />
          </Col>
          <Col span={6}>
            <Statistic
              title="Net Balance"
              value={Math.abs(balance)}
              precision={2}
              prefix="$"
              suffix={balance < 0 ? 'CR' : 'DR'}
              valueStyle={{ color: balance >= 0 ? '#3f8600' : '#cf1322' }}
            />
          </Col>
        </Row>
      </Card>

      {/* Ledger Table */}
      <Card style={{ marginTop: 16 }}>
        <Table
          dataSource={linesWithBalance}
          columns={columns}
          rowKey="id"
          loading={loading}
          pagination={{
            pageSize: 50,
            showSizeChanger: true,
            showTotal: (total) => `Total ${total} transactions`,
            pageSizeOptions: ['20', '50', '100', '200']
          }}
          scroll={{ x: 1200 }}
          size="small"
          bordered
        />
      </Card>
    </div>
  );
};

export default GeneralLedger;